import React, { useEffect, useState } from "react";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Typography,
} from "@mui/material";
import { USER_FACING_ERROR_MESSAGES } from "../utils/userFacingError";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const OIDC_CAC_LOGIN_PATH = "/api/auth/oidc/login";
const DIRECT_CAC_LOGIN_PATH = "/api/auth/direct-cac/login";
const PLATFORM_SESSION_URL = `${API_BASE_URL}/api/auth/oidc/platform-session`;
const PLATFORM_ONE_HELP_URL = "https://p1.dso.mil/";

type CacMode = "redirect" | "platform_jwt";

type CacAuthError =
  | "cac_login_cancelled"
  | "cac_login_failed"
  | "cac_service_unavailable"
  | "cac_assurance_required"
  | "cac_not_authorized";

const CAC_ERROR_MESSAGES: Record<CacAuthError, string> = {
  cac_login_cancelled: USER_FACING_ERROR_MESSAGES.cac_login_cancelled,
  cac_login_failed: USER_FACING_ERROR_MESSAGES.cac_login_failed,
  cac_service_unavailable: USER_FACING_ERROR_MESSAGES.cac_service_unavailable,
  cac_assurance_required: USER_FACING_ERROR_MESSAGES.cac_assurance_required,
  cac_not_authorized: USER_FACING_ERROR_MESSAGES.cac_not_authorized,
};

interface CacMethod {
  available: boolean;
  label: string;
  mode: CacMode;
  requiresPlatformOneAccount: boolean;
  loginUrl: string;
}

interface CacLoginProps {
  onAvailabilityChange?: (available: boolean) => void;
  onAuthSuccess?: (email: string) => void;
  navigate?: (url: string) => void;
}

function readCacMethod(
  payload: unknown,
  field: "cac_oidc" | "cac_oidc_fallback" = "cac_oidc",
): CacMethod | null {
  if (!payload || typeof payload !== "object") return null;

  const cacOidc = (
    payload as { cac_oidc?: unknown; cac_oidc_fallback?: unknown }
  )[field];
  if (!cacOidc || typeof cacOidc !== "object") return null;

  const {
    available,
    label,
    mode,
    requires_platform_one_account,
    login_url,
  } = cacOidc as {
    available?: unknown;
    label?: unknown;
    mode?: unknown;
    requires_platform_one_account?: unknown;
    login_url?: unknown;
  };
  if (available !== true) return null;
  if (mode !== undefined && mode !== "redirect" && mode !== "platform_jwt") {
    return null;
  }

  const safeLabel =
    typeof label === "string" && label.trim() && label.length <= 80
      ? label.trim()
      : "Sign in with CAC";
  const safeLoginPath =
    login_url === DIRECT_CAC_LOGIN_PATH
      ? DIRECT_CAC_LOGIN_PATH
      : OIDC_CAC_LOGIN_PATH;
  return {
    available: true,
    label: safeLabel,
    mode: mode === "platform_jwt" ? "platform_jwt" : "redirect",
    requiresPlatformOneAccount: requires_platform_one_account === true,
    loginUrl: `${API_BASE_URL}${safeLoginPath}`,
  };
}

function readCallbackError(): string | null {
  const url = new URL(window.location.href);
  const errorCode = url.searchParams.get("auth_error");
  if (!errorCode) return null;

  return (
    CAC_ERROR_MESSAGES[errorCode as CacAuthError] ??
    USER_FACING_ERROR_MESSAGES.cac_login_failed
  );
}

function clearCallbackError(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("auth_error")) return;

  url.searchParams.delete("auth_error");
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}

const navigateToCacLogin = (url: string): void => {
  window.location.assign(url);
};

const CacLogin: React.FC<CacLoginProps> = ({
  onAvailabilityChange,
  onAuthSuccess,
  navigate = navigateToCacLogin,
}) => {
  const [method, setMethod] = useState<CacMethod | null>(null);
  const [fallbackMethod, setFallbackMethod] = useState<CacMethod | null>(null);
  const [callbackError] = useState(readCallbackError);
  const [platformError, setPlatformError] = useState<string | null>(null);
  const [platformLoading, setPlatformLoading] = useState(false);

  useEffect(clearCallbackError, []);

  useEffect(() => {
    let active = true;
    onAvailabilityChange?.(false);

    const loadAuthMethods = async (): Promise<void> => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/methods`, {
          credentials: "include",
        });
        if (!response.ok) return;

        const payload = await response.json();
        const cacMethod = readCacMethod(payload);
        if (!active || !cacMethod) return;

        setMethod(cacMethod);
        setFallbackMethod(readCacMethod(payload, "cac_oidc_fallback"));
        onAvailabilityChange?.(true);
      } catch {
        // CAC discovery is optional. Email and authenticator login stay usable.
      }
    };

    void loadAuthMethods();
    return () => {
      active = false;
    };
  }, [onAvailabilityChange]);

  const handleCacLogin = async (selectedMethod: CacMethod): Promise<void> => {
    if (selectedMethod.mode === "redirect") {
      navigate(selectedMethod.loginUrl);
      return;
    }

    setPlatformLoading(true);
    setPlatformError(null);
    try {
      const response = await fetch(PLATFORM_SESSION_URL, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        const errorCode = response.headers.get("X-Auth-Error") as CacAuthError;
        setPlatformError(
          CAC_ERROR_MESSAGES[errorCode] ??
            USER_FACING_ERROR_MESSAGES.cac_login_failed,
        );
        return;
      }
      const payload = await response.json();
      const email =
        payload && typeof payload.email === "string" ? payload.email : null;
      if (payload?.status !== "authenticated" || !email) {
        setPlatformError(USER_FACING_ERROR_MESSAGES.cac_login_failed);
        return;
      }
      onAuthSuccess?.(email);
    } catch {
      setPlatformError(USER_FACING_ERROR_MESSAGES.cac_service_unavailable);
    } finally {
      setPlatformLoading(false);
    }
  };

  if (!method && !callbackError && !platformError) return null;

  return (
    <Box sx={{ mt: 2 }}>
      {callbackError && (
        <Alert severity="warning" sx={{ mb: method ? 2 : 0 }}>
          {callbackError}
        </Alert>
      )}
      {platformError && (
        <Alert severity="warning" sx={{ mb: method ? 2 : 0 }}>
          {platformError}
        </Alert>
      )}
      {method && (
        <>
          <Button
            variant="contained"
            fullWidth
            startIcon={<BadgeOutlinedIcon />}
            disabled={platformLoading}
            onClick={() => void handleCacLogin(method)}
          >
            {platformLoading ? <CircularProgress size={24} /> : method.label}
          </Button>
          {method.requiresPlatformOneAccount && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.75, px: 1, textAlign: "center" }}
            >
              Requires a Platform One account with your CAC registered.{" "}
              <Link
                href={PLATFORM_ONE_HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more about Platform One registration
              </Link>
              .
            </Typography>
          )}
          {fallbackMethod && (
            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
              disabled={platformLoading}
              onClick={() => void handleCacLogin(fallbackMethod)}
            >
              {fallbackMethod.label}
            </Button>
          )}
          <Divider sx={{ mt: 2 }}>Other sign-in options</Divider>
        </>
      )}
    </Box>
  );
};

export default CacLogin;
